// frontend/src/components/CreatorLanding.js
import React, { useState } from 'react';
import HamburgerNav from './HamburgerNav';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import './CreatorLanding.css';

const CreatorLanding = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    instagram: '',
    tripCount: '',
    readyToPay: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // If they're ready to pay, proceed with payment
      if (formData.readyToPay === 'yes') {
        // Try to save to Firebase but don't let it block payment
        try {
          await addDoc(collection(db, 'creatorApplications'), {
            ...formData,
            timestamp: serverTimestamp(),
            status: 'pending_payment'
          });
          console.log('Firebase save successful');
        } catch (firebaseError) {
          console.error('Firebase save failed (continuing to payment):', firebaseError);
          // Continue anyway - we'll have the data from Stripe webhook
        }

        // Go directly to Stripe checkout (like Gemma's page)
        console.log('Starting Stripe checkout...');
        console.log('Backend URL:', process.env.REACT_APP_BACKEND_URL);

        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/create-creator-checkout`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email: formData.email,
              name: formData.name,
              instagram: formData.instagram,
              tripCount: formData.tripCount
            })
          });

          if (!response.ok) {
            const errorText = await response.text();
            console.error('Response not OK:', response.status, errorText);
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          console.log('Checkout response:', data);

          if (data.url) {
            window.location.href = data.url;
          } else {
            throw new Error('No checkout URL received');
          }
        } catch (stripeError) {
          console.error('Error creating checkout session:', stripeError);
          alert(`Error processing payment: ${stripeError.message}. Please check the console for details.`);
          setIsSubmitting(false);
        }
      } else {
        // They have questions or are still thinking - send email
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/partnership-inquiry`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            message: `Creator interest - Trip count: ${formData.tripCount}, Instagram: ${formData.instagram || 'Not provided'}, Status: Has questions`,
            partnershipType: 'traveler',
            subject: 'BackPass Creator Program - Get in Touch For Next Steps'
          }),
        });

        if (response.ok) {
          alert('Thanks for your interest! We\'ll be in touch within 24 hours to answer your questions.');
          // Reset form
          setFormData({
            name: '',
            email: '',
            phone: '',
            instagram: '',
            tripCount: '',
            readyToPay: ''
          });
        } else {
          alert('Error sending your information. Please try again or email us directly.');
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Error submitting application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="creator-landing-container" style={{ marginTop: '-20px' }}>
      {/* Hero Section */}
      <section
        className="creator-hero-section"
        style={{
          background: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(7, 34, 49, 0.7)), url('/images/archedbackground.webp') center/cover`
        }}
      >
        <HamburgerNav animationDelay="0.4s" />

        {/* Urgency Banner */}
        <div className="urgency-banner">
          ⚡ 3 Spots Remaining
        </div>

        <div className="creator-hero-content">
          <a href="/" className="hero-logo">
            <img src="/images/logodownsized.webp" alt="BackPass Logo" className="logo" />
          </a>

          <h1 className="creator-title-caveat">Get Paid to Travel</h1>
          <h2 className="creator-subtitle">
            Turn Your Past Adventures Into $500+ Monthly Income
          </h2>

          <div className="value-props">
            <div className="value-prop">
              <span className="value-icon">📝</span>
              <span>20 minutes to start</span>
            </div>
            <div className="value-prop">
              <span className="value-icon">💰</span>
              <span>$25 per sale</span>
            </div>
            <div className="value-prop">
              <span className="value-icon">🔄</span>
              <span>Earn for 3 years</span>
            </div>
          </div>

          <div className="hero-cta-buttons">
            <button
              className="primary-cta"
              onClick={() => document.getElementById('creator-form').scrollIntoView({ behavior: 'smooth' })}
            >
              Claim Your Spot - $100
            </button>
            <button
              className="secondary-cta"
              onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
            >
              See How It Works
            </button>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="how-it-works-section">
        <h2 className="section-title">How It Works</h2>

        <div className="steps-container">
          <div className="step">
            <div className="step-number">1</div>
            <h3>Document Your Trip</h3>
            <p>Share where you stayed, hidden gems you found, and money-saving tips</p>
            <span className="time-estimate">Document Your Trip as You Go or Use Your Previous Experience</span>
          </div>

          <div className="step">
            <div className="step-number">2</div>
            <h3>We List Your Route</h3>
            <p>Professional listing in our store, marketed to thousands of travelers</p>
            <span className="time-estimate">Live in 24 hours</span>
          </div>

          <div className="step">
            <div className="step-number">3</div>
            <h3>Earn Commission</h3>
            <p>$25 for every traveler who books your route, paid monthly</p>
            <span className="time-estimate">For 3 full years</span>
          </div>
        </div>
      </section>

      {/* Earnings Calculator */}
      <section className="calculator-section">
        <h2 className="section-title">Your Earning Potential</h2>
        <div className="calculator">
          <div className="slider-container">
            <label>Routes sold per month: <span id="sales-display">10</span></label>
            <input
              type="range"
              min="1"
              max="50"
              defaultValue="10"
              onChange={(e) => {
                document.getElementById('sales-display').textContent = e.target.value;
                document.getElementById('monthly-earnings').textContent = e.target.value * 25;
                document.getElementById('yearly-earnings').textContent = e.target.value * 25 * 12;
                document.getElementById('total-earnings').textContent = e.target.value * 25 * 36;
              }}
            />
          </div>

          <div className="earnings-display">
            <div className="earning-stat">
              <span className="earning-label">Monthly</span>
              <span className="earning-amount">$<span id="monthly-earnings">250</span></span>
            </div>
            <div className="earning-stat">
              <span className="earning-label">Yearly</span>
              <span className="earning-amount">$<span id="yearly-earnings">3,000</span></span>
            </div>
            <div className="earning-stat featured">
              <span className="earning-label">3 Year Total</span>
              <span className="earning-amount">$<span id="total-earnings">9,000</span></span>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="creator-form" className="form-section">
        <div className="form-container">
          <h2 className="form-title">Lock In Your Founding Creator Spot</h2>
          <p className="form-subtitle">First 20 creators get 50% commission for 3 years</p>

          <form onSubmit={handleFormSubmit}>
            <input
              type="text"
              name="name"
              placeholder="Full Name*"
              value={formData.name}
              onChange={handleInputChange}
              required
            />

            <input
              type="email"
              name="email"
              placeholder="Email Address*"
              value={formData.email}
              onChange={handleInputChange}
              required
            />

            <input
              type="tel"
              name="phone"
              placeholder="Phone Number*"
              value={formData.phone}
              onChange={handleInputChange}
              required
            />

            <input
              type="text"
              name="instagram"
              placeholder="Instagram Handle (optional)"
              value={formData.instagram}
              onChange={handleInputChange}
            />

            <select
              name="tripCount"
              value={formData.tripCount}
              onChange={handleInputChange}
              required
            >
              <option value="">How many trips can you document?*</option>
              <option value="1-2">1-2 trips</option>
              <option value="3-5">3-5 trips</option>
              <option value="5+">5+ trips</option>
            </select>

            <select
              name="readyToPay"
              value={formData.readyToPay}
              onChange={handleInputChange}
              required
            >
              <option value="">Ready to start earning?*</option>
              <option value="yes">Yes, let's do this!</option>
              <option value="questions">Yes, but I have questions</option>
            </select>

            <button type="submit" className="submit-button" disabled={isSubmitting}>
              {isSubmitting ? 'Processing...' :
                formData.readyToPay === 'yes' ? 'Continue to Payment →' :
                formData.readyToPay ? 'Get in Touch →' :
                'Continue to Payment →'}
            </button>

            <p className="guarantee-text">
              Secure payment by Stripe
            </p>
          </form>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <h2 className="section-title">Quick FAQ</h2>

        <div className="faq-container">
          <details className="faq-item">
            <summary>Is there any sort of refferral program?</summary>
            <p>No, creators sell directly to travelers. No recruiting, no tiers. You provide curated backpacking. We promote it.</p>
          </details>

          <details className="faq-item">
            <summary>What do I need to do after I travel?</summary>
            <p>Nothing. These aren't guiding tours. Basically you're being paid to help others save money and streamline the best experiences.</p>
          </details>

          <details className="faq-item">
            <summary>Why $100 upfront?</summary>
            <p>Ensures serious creators only. Covers our platform costs. You get $25 everytime a pass is sold and you break even after 4 sales.</p>
          </details>

          <details className="faq-item">
            <summary>How do I get paid?</summary>
            <p>Monthly payouts via Stripe. You see all sales in real-time.</p>
          </details>

          <details className="faq-item">
            <summary>What do I need to provide?</summary>
            <p>Pick 2 hostels in atleast 2 cities, one that's chill and one that's lit, and provide tips to make it worth the traveler's time. We will handle the rest.</p>
          </details>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta-section">
        <h2>Ready to Turn Your Adventures Into Income?</h2>
        <p>Join the first 20 founding creators and lock in 50% commission for 3 years</p>
        <button
          className="final-cta-button"
          onClick={() => document.getElementById('creator-form').scrollIntoView({ behavior: 'smooth' })}
        >
          Claim Your Spot Now
        </button>
      </section>
    </div>
  );
};

export default CreatorLanding;